/**
 * Typed API client. The Vite dev server proxies /api/* to localhost:8080,
 * and in production the frontend is served from the same origin as the API.
 *
 * The token is stored in localStorage and attached to every request.
 */

const TOKEN_KEY = 'agentmeeting.token';

export interface User {
  id: string;
  email: string;
  name?: string;
  email_verified: boolean;
  credits_cents: number;
  plan: string;
}

export interface ProviderInfo {
  name: string;
  models: { id: string; name: string; context_len: number }[];
}

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(t: string | null) {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else   localStorage.removeItem(TOKEN_KEY);
  } catch { /* noop */ }
}

class HTTPError extends Error {
  status: number;
  body: any;
  constructor(status: number, body: any) {
    super(`HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(method: string, path: string, body?: any): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const tok = getToken();
  if (tok) headers['Authorization'] = `Bearer ${tok}`;
  const res = await fetch(path, {
    method,
    headers,
    body: body == null ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  const parsed = text ? safeParse(text) : null;
  if (!res.ok) throw new HTTPError(res.status, parsed);
  return parsed as T;
}
function safeParse(s: string) { try { return JSON.parse(s); } catch { return s; } }

// ─── auth ──────────────────────────────────────────────
export const auth = {
  signup: (input: { email: string; password?: string; name?: string }) =>
    request<{ token: string; user: User }>('POST', '/api/auth/signup', input),
  signin: (input: { email: string; password?: string; use_magic?: boolean }) =>
    request<{ token: string; user: User } | { sent: boolean; dev_token?: string }>(
      'POST', '/api/auth/signin', input
    ),
  verifyMagic: (token: string) =>
    request<{ token: string; user: User }>('GET', `/api/auth/verify?token=${encodeURIComponent(token)}`),
  me: () => request<User>('GET', '/api/me'),
};

// ─── providers ─────────────────────────────────────────
export const providers = {
  list: () => request<{ providers: ProviderInfo[] }>('GET', '/api/providers'),
};

// ─── agents ────────────────────────────────────────────
export interface AgentRecord {
  id: string;
  name: string;
  persona: string;
  stance: string;
  stance_label: string;
  short: string;
  provider: string;
  model: string;
}
export const agents = {
  list:   () => request<{ agents: AgentRecord[] }>('GET', '/api/agents'),
  create: (a: Partial<AgentRecord> & { name: string; provider: string; model: string }) =>
    request<{ id: string }>('POST', '/api/agents', a),
  update: (id: string, a: Partial<AgentRecord>) =>
    request<void>('PATCH', `/api/agents/${id}`, a),
  remove: (id: string) => request<void>('DELETE', `/api/agents/${id}`),
};

// ─── meetings ──────────────────────────────────────────
export interface MeetingRecord {
  id: string;
  title: string;
  topic: string;
  type: string;
  status: string;
  current_round: number;
  total_rounds: number;
  budget_cents: number;
  spent_cents: number;
  created_at: string;
}
export interface ParticipantRecord {
  id: string;
  kind: 'agent' | 'user';
  ref_id: string;
  role: string;
  seat_no: number;
  name: string;
  short: string;
  stance: string;
  provider: string;
  model: string;
}
export const meetings = {
  list:   () => request<{ meetings: MeetingRecord[] }>('GET', '/api/meetings'),
  get:    (id: string) => request<MeetingRecord & { config: any }>('GET', `/api/meetings/${id}`),
  create: (m: { title: string; topic: string; type: 'debate'|'decision'|'consult'; budget_cents?: number }) =>
    request<{ id: string }>('POST', '/api/meetings', m),
  listParticipants: (id: string) =>
    request<{ participants: ParticipantRecord[] }>('GET', `/api/meetings/${id}/participants`),
  addParticipant: (id: string, p: { kind: 'agent'|'user'; ref_id: string; role?: string; seat_no?: number }) =>
    request<{ id: string }>('POST', `/api/meetings/${id}/participants`, p),
  removeParticipant: (meetingId: string, participantId: string) =>
    request<{ ok: boolean }>('DELETE', `/api/meetings/${meetingId}/participants/${participantId}`),
  saveAgenda: (id: string, agenda: { rounds: any[]; window_size?: number; summary_interval?: number; termination?: string[] }) =>
    request<void>('PUT', `/api/meetings/${id}/agenda`, agenda),
  start:  (id: string) => request<void>('POST', `/api/meetings/${id}/start`, {}),
  pause:  (id: string) => request<void>('POST', `/api/meetings/${id}/pause`, {}),
  end:    (id: string) => request<void>('POST', `/api/meetings/${id}/end`, {}),
  messages: (id: string) =>
    request<{ messages: any[] }>('GET', `/api/meetings/${id}/messages`),
};

// ─── live stream ───────────────────────────────────────
export interface ServerEvent {
  type: 'meeting.state' | 'turn.start' | 'turn.token' | 'turn.end'
      | 'agenda.advance' | 'vote.cast' | 'resolution.ready' | 'system.error';
  payload: any;
}

/**
 * Connect to a meeting's WebSocket stream. Returns a function that closes the
 * connection. The onEvent callback receives every server event.
 */
export function streamMeeting(
  meetingID: string,
  onEvent: (ev: ServerEvent) => void,
  onClose?: () => void,
): () => void {
  const tok = getToken();
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = `${proto}//${location.host}/api/meetings/${meetingID}/stream${tok ? `?token=${encodeURIComponent(tok)}` : ''}`;
  const ws = new WebSocket(url);
  ws.onmessage = (msg) => {
    try { onEvent(JSON.parse(msg.data) as ServerEvent); } catch { /* skip */ }
  };
  ws.onclose = () => { if (onClose) onClose(); };
  return () => ws.close();
}

// ─── starter packs ─────────────────────────────────────
export interface StarterPackInfo {
  id: string;
  name: string;
  description: string;
  meeting_type: string;
  default_topic: string;
  agents: any[];
  rounds: any[];
}
export const starter = {
  list: () => request<{ packs: StarterPackInfo[] }>('GET', '/api/starter-packs'),
  seed: (packId: string, body?: { topic?: string; title?: string }) =>
    request<{ meeting_id: string; agent_ids: string[]; title: string; topic: string }>(
      'POST', `/api/starter-packs/${encodeURIComponent(packId)}`, body || {}),
};

// ─── forks ─────────────────────────────────────────────
export const forks = {
  list: (id: string) => request<{ forks: any[] }>('GET', `/api/meetings/${id}/forks`),
  create: (id: string, body: { from_message_id: string; title?: string; mutations?: any }) =>
    request<{ id: string; parent_id: string }>('POST', `/api/meetings/${id}/fork`, body),
};

// ─── resolution (votes + minutes) ──────────────────────
export const resolution = {
  listVotes: (id: string) =>
    request<{ votes: Record<string, any>; tally: Record<string, number> }>(
      'GET', `/api/meetings/${id}/votes`),
  castVote: (id: string, body: { choice: string; rationale?: string; as_participant_id?: string }) =>
    request<{ id: string }>('POST', `/api/meetings/${id}/votes`, body),
  castAgentVotes: (id: string, body?: { participant_ids?: string[] }) =>
    request<{
      votes_cast: number;
      results: Array<{ participant_id: string; name: string; choice: string; rationale: string; cost_cents: number }>;
      errors: Array<{ participant_id: string; error: string }>;
    }>('POST', `/api/meetings/${id}/agent-votes`, body || {}),
  getMinutes: (id: string) =>
    request<{ minutes_md: string; generated_at: string }>('GET', `/api/meetings/${id}/minutes`),
  generateMinutes: (id: string) =>
    request<{ minutes_md: string; tokens_in?: number; tokens_out?: number; cost_cents?: number }>(
      'POST', `/api/meetings/${id}/minutes`),
};

// ─── me + BYOK API keys ────────────────────────────────
// NOTE: PATCH /api/me + /api/me/api-keys routes are NOT yet implemented backend-side
// (Wave 5 backlog). Callers should catch HTTPError(404) and treat as "coming soon".
export const me = {
  update: (body: { name?: string; email?: string }) =>
    request<User>('PATCH', '/api/me', body),
  listApiKeys: () => request<{ keys: any[] }>('GET', '/api/me/api-keys'),
  addApiKey: (body: { provider: string; key: string; label?: string }) =>
    request<{ id: string }>('POST', '/api/me/api-keys', body),
  deleteApiKey: (id: string) => request<void>('DELETE', `/api/me/api-keys/${id}`),
};

// ─── billing ───────────────────────────────────────────
export const billing = {
  status: () => request<{
    plan: string;
    credits_cents: number;
    stripe_customer_id?: string;
    subscription_id?: string;
    current_period_end?: string;
  }>('GET', '/api/billing/status'),
  checkout: (plan: 'pro' | 'team' | 'enterprise') =>
    request<{ url: string }>('POST', '/api/billing/checkout', { plan }),
  portal: () => request<{ url: string }>('POST', '/api/billing/portal', {}),
};

// ─── knowledge (file uploads) ──────────────────────────
// NOTE: HTTP wrappers around server/internal/knowledge/store.go are NOT yet in api.go
// (Wave 5 backlog). Callers should handle HTTPError(404) gracefully.
export const knowledge = {
  list: () => request<{ files: any[] }>('GET', '/api/knowledge'),
  upload: async (file: File): Promise<{ id: string }> => {
    const tok = getToken();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch('/api/knowledge', {
      method: 'POST',
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      body: fd,
    });
    const text = await res.text();
    const parsed = text ? safeParse(text) : null;
    if (!res.ok) throw new HTTPError(res.status, parsed);
    return parsed as { id: string };
  },
  remove: (id: string) => request<void>('DELETE', `/api/knowledge/${id}`),
};

export const admin = {
  listUsers: (q?: { limit?: number; offset?: number }) => {
    const p = new URLSearchParams();
    if (q?.limit) p.set('limit', String(q.limit));
    if (q?.offset) p.set('offset', String(q.offset));
    const qs = p.toString();
    return request<{ users: any[]; total: number; limit: number; offset: number }>(
      'GET', '/api/admin/users' + (qs ? '?' + qs : ''));
  },
  getUser: (id: string) =>
    request<{ user: any; transactions: any[] }>('GET', `/api/admin/users/${id}`),
  recharge: (id: string, body: { amount_cents: number; note?: string }) =>
    request<{ transaction_id: string; balance_after: number; amount_cents: number }>(
      'POST', `/api/admin/users/${id}/recharge`, body),
  patchUser: (id: string, body: { is_admin?: boolean; plan?: string }) =>
    request<void>('PATCH', `/api/admin/users/${id}`, body),
  listTransactions: (q?: { limit?: number; offset?: number; kind?: string }) => {
    const p = new URLSearchParams();
    if (q?.limit) p.set('limit', String(q.limit));
    if (q?.offset) p.set('offset', String(q.offset));
    if (q?.kind) p.set('kind', q.kind);
    const qs = p.toString();
    return request<{ transactions: any[] }>(
      'GET', '/api/admin/transactions' + (qs ? '?' + qs : ''));
  },
  listMeetings: (q?: { limit?: number; offset?: number }) => {
    const p = new URLSearchParams();
    if (q?.limit) p.set('limit', String(q.limit));
    if (q?.offset) p.set('offset', String(q.offset));
    const qs = p.toString();
    return request<{ meetings: any[] }>(
      'GET', '/api/admin/meetings' + (qs ? '?' + qs : ''));
  },
  stats: () => request<{
    total_users: number; total_meetings: number; total_messages: number;
    total_spent_cents: number; total_credits_outstanding_cents: number;
  }>('GET', '/api/admin/stats'),
};

// ─── expert library (agent templates) ─────────────────
export interface AgentTemplate {
  id: string;
  slug: string;
  name: string;
  short: string;
  category: 'professional' | 'celebrity';
  tags: string[];
  stance: string;
  stance_label: string;
  one_liner: string;
  persona: string;
  default_provider: string;
  default_model: string;
  source_attribution: string;
}
export const expertLibrary = {
  list: (q?: { category?: 'professional' | 'celebrity'; tag?: string }) => {
    const p = new URLSearchParams();
    if (q?.category) p.set('category', q.category);
    if (q?.tag) p.set('tag', q.tag);
    const qs = p.toString();
    return request<{ templates: AgentTemplate[] }>(
      'GET', '/api/expert-library' + (qs ? '?' + qs : ''));
  },
  instantiate: (slug: string, body?: { name?: string; provider?: string; model?: string }) =>
    request<{ id: string; reused: boolean }>(
      'POST', `/api/expert-library/${encodeURIComponent(slug)}/instantiate`, body || {}),
};

export { HTTPError };
